import { FormItem, useModal } from '@billy/design-system'
import { Search } from '@shinetools/lumen-react'
import type { ChangeEvent } from 'react'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useFetchCompanies } from '@modules-deprecated/app/companies/hooks/useFetchCompanies'
import { Company } from '@modules-deprecated/app/companies/types'
import { getCompanyName } from '@modules-deprecated/app/companies/utils/getCompanyName'
import { ExpensesTrackingEvents } from '@views/receipts/enums/tracking'

// The @components barrel re-exports CreateContactModal, which renders this dropdown through the create-supplier modal.
// Moving the supplier modal out of views/ does not fix this; it can import from @components again once the barrel
// stops re-exporting CreateContactModal and ContactSelect (EXP-660).
// eslint-disable-next-line @billy/shine-accounting/consistent-alias-imports -- importing the barrel from here cycles back through CreateContactModal
import { UpgradeSubscriptionModal } from '../../../../../../../../../../../../components/Modal/upgradeSubscriptionModal'
import { ModalId } from '../../../../../../../../../../../../enums/modalId'
import { Scope } from '../../../../../../../../../../../../enums/scope'
import { TrackingContext } from '../../../../../../../../../../../../enums/trackingContext'
import { useComponentId } from '../../../../../../../../../../../../hooks/useComponentId'
import { useSegment } from '../../../../../../../../../../../../hooks/useSegment'
import { isAuthorized } from '../../../../../../../../../../../../utils/isAuthorized'

const MIN_SEARCH_LENGTH = 3

interface CompanySearchDropdownProps {
  error?: string
  name: string
  onNameChange: (name: string) => void
  onSelect: (company: Company) => void
}

const getCompanySearchItemLabel = (company: Company): string => {
  const companyName = getCompanyName(company) ?? company.id
  const addressParts = [company.street, company.zipcode, company.city].filter(Boolean)

  if (addressParts.length === 0) {
    return `${companyName} (${company.id})`
  }

  return `${companyName} (${company.id}) — ${addressParts.join(', ')}`
}

export const CompanySearchDropdown = ({ error, name, onNameChange, onSelect }: CompanySearchDropdownProps) => {
  const { t } = useTranslation()
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>()
  const { companies, isFetching } = useFetchCompanies(name)
  const { track } = useSegment()
  const upgradeModalId = useComponentId(ModalId.UpgradeSubscriptionModal)
  const { open: openUpgradeSubscriptionModal } = useModal(upgradeModalId)
  const companyNameLabel = t('suppliers.detail.form.company_name.label')
  const isRegistryLookupAuthorized = isAuthorized(Scope.ContactRegisterLookup)
  // `useFetchCompanies` keeps the previous search's results while a new one is in flight, so without
  // checking isFetching here the dropdown would show stale items that then flash into the new list.
  const shouldShowDropdown = isDropdownOpen && name.length >= MIN_SEARCH_LENGTH && !isFetching && companies.length > 0

  const handleSearchChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setSelectedCompanyId(undefined)
      onNameChange(event.target.value)
    },
    [onNameChange],
  )

  const handleOpenChange = useCallback((isOpen: boolean) => {
    setIsDropdownOpen(isOpen)
  }, [])

  const handleSearchSelect = useCallback(
    (companyId: string) => {
      const company = companies.find(({ id }) => id === companyId)

      if (!company) {
        return
      }

      if (!isRegistryLookupAuthorized) {
        openUpgradeSubscriptionModal()
        return
      }

      setSelectedCompanyId(companyId)
      track(ExpensesTrackingEvents.SupplierCompanyCreatedManually)
      onSelect(company)
    },
    [companies, isRegistryLookupAuthorized, onSelect, openUpgradeSubscriptionModal, track],
  )

  return (
    <>
      <FormItem error={error} label={companyNameLabel} name="supplier-company-name">
        {({ id }) => (
          <Search
            id={id}
            listboxLabel={t('company_select.dropdown.placeholder')}
            onChange={handleSearchChange}
            onOpenChange={handleOpenChange}
            onSelect={handleSearchSelect}
            open={shouldShowDropdown}
            placeholder={t('companies.company_search_field.placeholder')}
            size="small"
            value={name}
          >
            {companies.map((company) => (
              <Search.Item
                key={company.id}
                label={
                  isRegistryLookupAuthorized
                    ? getCompanySearchItemLabel(company)
                    : `${getCompanySearchItemLabel(company)} · ${t('company_select.dropdown.upgrade')}`
                }
                selected={company.id === selectedCompanyId}
                value={company.id}
              />
            ))}
          </Search>
        )}
      </FormItem>
      <UpgradeSubscriptionModal id={upgradeModalId} trackingContext={TrackingContext.CreateContact} />
    </>
  )
}
